import type { AddressQuery, House, LatLng } from '../types/house'
import { centroid, distanceMeters, parsePoint, pointInRing, rectangleAround, ringsTouch } from './geo'
import { deriveHouse } from './house'

const LOCATION_URL = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/free'
const BAG_WFS_URL = 'https://service.pdok.nl/lv/bag/wfs/v2_0'

export type LookupErrorKind = 'not-found' | 'network'

export class LookupError extends Error {
  kind: LookupErrorKind
  constructor(kind: LookupErrorKind) {
    super(kind)
    this.kind = kind
  }
}

interface LocationDoc {
  straatnaam: string
  huisnummer: number
  huisletter?: string
  huisnummertoevoeging?: string
  postcode: string
  woonplaatsnaam: string
  centroide_ll: string
  centroide_rd: string
}

interface PandFeature {
  geometry: { type: string; coordinates: unknown }
  properties: Record<string, unknown>
}

interface Pand {
  ring: LatLng[]
  props: Record<string, unknown>
}

function normaliseRing(coords: number[][]): LatLng[] {
  // Dutch coordinates: latitude (~51-53) is always larger than longitude (~3-7).
  return coords.map(([a, b]) => (a > b ? [a, b] : [b, a]) as LatLng)
}

function extractRing(feature: PandFeature): LatLng[] | null {
  const { type, coordinates } = feature.geometry
  if (type === 'Polygon') return normaliseRing((coordinates as number[][][])[0])
  if (type === 'MultiPolygon') return normaliseRing((coordinates as number[][][][])[0][0])
  return null
}

async function fetchLocation(query: AddressQuery, signal: AbortSignal): Promise<LocationDoc> {
  const postcode = query.postcode.replace(/\s/g, '').toUpperCase()
  const params = new URLSearchParams({
    q: `${postcode} ${query.number}${query.addition}`,
    rows: '10',
    fl: 'straatnaam,huisnummer,huisletter,huisnummertoevoeging,postcode,woonplaatsnaam,centroide_ll,centroide_rd',
  })
  params.append('fq', 'type:adres')
  params.append('fq', `postcode:${postcode}`)
  params.append('fq', `huisnummer:${query.number}`)

  let res: Response
  try {
    res = await fetch(`${LOCATION_URL}?${params.toString()}`, { signal })
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e
    throw new LookupError('network')
  }
  if (!res.ok) throw new LookupError('network')
  const json = (await res.json()) as { response?: { docs?: LocationDoc[] } }
  const docs = json.response?.docs ?? []
  if (docs.length === 0) throw new LookupError('not-found')

  const addition = query.addition.trim().toUpperCase()
  if (!addition) return docs.find((d) => !d.huisletter && !d.huisnummertoevoeging) ?? docs[0]
  return (
    docs.find(
      (d) =>
        (d.huisletter ?? '').toUpperCase() === addition ||
        (d.huisnummertoevoeging ?? '').toUpperCase() === addition,
    ) ?? docs[0]
  )
}

async function fetchPanden(rdX: number, rdY: number, signal: AbortSignal): Promise<Pand[]> {
  const r = 35
  const params = new URLSearchParams({
    service: 'WFS',
    version: '2.0.0',
    request: 'GetFeature',
    typeNames: 'bag:pand',
    outputFormat: 'application/json',
    srsName: 'EPSG:4326',
    count: '40',
    bbox: `${rdX - r},${rdY - r},${rdX + r},${rdY + r},EPSG:28992`,
  })
  const res = await fetch(`${BAG_WFS_URL}?${params.toString()}`, { signal })
  if (!res.ok) throw new Error('wfs')
  const json = (await res.json()) as { features?: PandFeature[] }
  return (json.features ?? [])
    .map((f) => ({ ring: extractRing(f), props: f.properties }))
    .filter((p): p is Pand => p.ring !== null && p.ring.length > 2)
}

function toNumber(value: unknown): number | undefined {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : undefined
}

export async function lookupHouse(query: AddressQuery, signal: AbortSignal): Promise<House> {
  const doc = await fetchLocation(query, signal)
  const [lng, lat] = parsePoint(doc.centroide_ll)
  const [rdX, rdY] = parsePoint(doc.centroide_rd)
  const suffix = `${doc.huisletter ?? ''}${doc.huisnummertoevoeging ? `-${doc.huisnummertoevoeging}` : ''}`
  const base = {
    street: doc.straatnaam,
    houseNumber: `${doc.huisnummer}${suffix}`,
    postcode: doc.postcode,
    city: doc.woonplaatsnaam,
    lat,
    lng,
  }

  let panden: Pand[] = []
  try {
    panden = await fetchPanden(rdX, rdY, signal)
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e
  }

  if (panden.length === 0) {
    return deriveHouse({
      ...base,
      footprint: rectangleAround(lat, lng, 7, 10),
      buildYear: 1985,
      units: 1,
      neighbours: 2,
      source: 'bag',
    })
  }

  const point: LatLng = [lat, lng]
  // Prefer buildings that actually contain homes (skip sheds and garages).
  const residential = panden.filter((p) => (toNumber(p.props.aantal_verblijfsobjecten) ?? 0) > 0)
  const pool = residential.length > 0 ? residential : panden
  const target =
    pool.find((p) => pointInRing(point, p.ring)) ??
    [...pool].sort((a, b) => distanceMeters(point, centroid(a.ring)) - distanceMeters(point, centroid(b.ring)))[0]
  const neighbours = panden.filter((p) => p !== target && ringsTouch(target.ring, p.ring)).length

  return deriveHouse({
    ...base,
    footprint: target.ring,
    buildYear: toNumber(target.props.bouwjaar) ?? 1985,
    livingArea: toNumber(target.props.oppervlakte_max) ?? toNumber(target.props.oppervlakte_min),
    units: toNumber(target.props.aantal_verblijfsobjecten) ?? 1,
    neighbours,
    source: 'bag',
  })
}
